'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, delay, groupItems } from '@/helpers/common';
import { MediaType } from '@/helpers/enums';
import { useChunked } from '@/hooks/chunkedSlides';
import useEventListener from '@/hooks/eventListener';
import { useResponsive } from '@/modules/client';
import { useGetCommonStore } from '@/store';
import { Arrows } from './components/arrows';
import { Slider } from './components/Slider/Slider';
import type { ScSliderProps } from './components/Slider/styled';
import type { ScArrowProps, SlideGrid, SliderWithArrowProps } from './helpers';
import { ScSliderContainer } from './styled';

export type SliderHelpers = Partial<{
  onScrollToStart: () => void;
}>;

interface SliderProps<T> extends ScArrowProps, Omit<ScSliderProps, 'padding'> {
  pending?: boolean;
  pendingSlide?: React.ComponentType<{}>;
  slides: T[];
  slideGrid?: SlideGrid;
  slide: React.ComponentType<T & SliderHelpers>;
  arrows?: SliderWithArrowProps;
  lazyLoadingOf?: number;
  autoplay?: {
    delay: number;
    pauseOnMouseEnter?: boolean;
  };
  loop?: boolean;
  pagination?: boolean;
  paginationInset?: string;
  initialSlide?: number;
  dataTestId?: string;
}

export function NativeSlider<T>({
  slides,
  slide: Slide,
  arrows,
  loop,
  autoplay,
  pagination,
  initialSlide,
  slideGrid,
  lazyLoadingOf,
  paginationInset,
  dataTestId,
  ...props
}: SliderProps<T>) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [isBeginning, setIsBeginning] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [hideArrows, setHideArrows] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideWidth, setSlideWidth] = useState(0);

  const { currentMediaType } = useResponsive();
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const { cols = 1, rows = 1 } = useMemo(() => {
    switch (currentMediaType) {
      case MediaType.DESKTOP_L:
        return {
          cols: slideGrid?.desktopL?.cols,
          rows: slideGrid?.desktopL?.rows,
        };
      case MediaType.DESKTOP:
        return {
          cols: slideGrid?.desktop?.cols,
          rows: slideGrid?.desktop?.rows,
        };
      case MediaType.TABLET:
        return {
          cols: slideGrid?.tablet?.cols,
          rows: slideGrid?.tablet?.rows,
        };
      case MediaType.MOBILE:
        return {
          cols: slideGrid?.mobile?.cols,
          rows: slideGrid?.mobile?.rows,
        };
      case MediaType.MOBILE_S:
        return {
          cols: slideGrid?.mobileS?.cols,
          rows: slideGrid?.mobileS?.rows,
        };
      default:
        return {
          cols: 1,
          rows: 1,
        };
    }
  }, [currentMediaType, slideGrid]);

  const gridSlides = useMemo(
    () => groupItems(slides, rows, cols),
    [cols, rows, slides]
  );

  const [isEnd, setIsEnd] = useState(gridSlides.length === 1);

  const isOne = useMemo(
    () => gridSlides == null || gridSlides.length <= 1,
    [gridSlides]
  );

  const handleArrows = useCallback(async () => {
    if (hideArrows) return;
    setHideArrows(true);
    await delay(500);
    setHideArrows(false);
  }, [hideArrows]);

  const handleScrollTo = useCallback((scrollLeft: number) => {
    sliderRef.current?.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }, []);

  const handleScrollToStart = useCallback(() => {
    handleScrollTo(0);
  }, [handleScrollTo]);

  const handlePrevClick = useCallback(() => {
    const track = sliderRef.current;
    if (track) {
      handleArrows();

      const scrollTarget =
        track.scrollLeft <= 0 && loop
          ? track.scrollWidth - track.clientWidth
          : track.scrollLeft - slideWidth;

      handleScrollTo(scrollTarget);
    }
  }, [handleArrows, loop, handleScrollTo, slideWidth]);

  const handleNextClick = useCallback(() => {
    const track = sliderRef.current;

    if (track == null) return;

    handleArrows();

    const scrollTarget =
      track.scrollLeft >= track.scrollWidth - track.clientWidth && loop
        ? 0
        : track.scrollLeft + slideWidth;

    handleScrollTo(scrollTarget);
  }, [handleArrows, loop, handleScrollTo, slideWidth]);

  const handleScroll = debounce(() => {
    const track = sliderRef.current;
    if (track == null) return;

    const index = Math.round(track.scrollLeft / slideWidth);

    setCurrentSlideIndex(index);
    setIsBeginning(index === 0 && !loop);
    setIsEnd(index === gridSlides.length - 1 && !loop);
  }, 100);

  useEffect(() => {
    if (!isHovering && autoplay?.pauseOnMouseEnter) {
      const autoScrollInterval = setInterval(() => {
        handleNextClick();
      }, autoplay.delay);

      return () => {
        clearInterval(autoScrollInterval);
      };
    }
  }, [autoplay, handleNextClick, isHovering]);

  useEffect(() => {
    initialSlide && handleScrollTo(initialSlide * slideWidth);
  }, [handleScrollTo, initialSlide, slideWidth]);

  const perChunk = useMemo(() => {
    if (lazyLoadingOf == null || props?.slidesPerView == null)
      return gridSlides?.length || 0;

    return Math.floor(props.slidesPerView) * lazyLoadingOf;
  }, [lazyLoadingOf, props.slidesPerView, gridSlides?.length]);

  const { itemsChunk: slidesChunk, nextChunk } = useChunked({
    items: gridSlides,
    perChunk,
  });

  const handleNativeScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        const target = event.target as HTMLDivElement;

        const scrollOffset =
          target.scrollWidth - target.clientWidth - target.scrollLeft;
        const perChunkOffset = target.scrollWidth / perChunk;

        const shouldLoadNextChunk = scrollOffset < perChunkOffset;

        if (shouldLoadNextChunk) {
          nextChunk();
        }
      }, 150);
    },
    [nextChunk, perChunk]
  );

  useEventListener('scroll', handleScroll, sliderRef);

  return gridSlides ? (
    <ScSliderContainer
      data-testid={dataTestId}
      isFullScreen={isMobileDevice}
      onMouseEnter={
        autoplay?.pauseOnMouseEnter ? () => setIsHovering(true) : undefined
      }
      onMouseLeave={
        autoplay?.pauseOnMouseEnter ? () => setIsHovering(false) : undefined
      }
      paginationInset={paginationInset}
      ref={(node: HTMLDivElement | null) => {
        if (node) {
          setSlideWidth(node.clientWidth);
        }
      }}
    >
      {arrows && (
        <Arrows
          arrows={arrows}
          handleNextClick={handleNextClick}
          handlePrevClick={handlePrevClick}
          hideArrows={hideArrows}
          isBeginning={isBeginning}
          isEnd={isEnd}
          isOne={isOne}
          loop={loop}
        />
      )}

      <Slider
        currentSlideIndex={currentSlideIndex}
        handleNativeScroll={handleNativeScroll}
        itemComponent={Slide}
        sliderHelpers={{
          onScrollToStart: handleScrollToStart,
        }}
        sliderRef={sliderRef}
        slidesChunk={slidesChunk}
        slidesPerView={props.slidesPerView}
      />

      {/* pagination */}
      {pagination && (
        <div className="pagination">
          {slidesChunk.map((_, index) => (
            <div
              className={`pagination-bullet ${
                index === currentSlideIndex ? 'active' : ''
              }`}
              key={index}
              onClick={() => handleScrollTo(index * slideWidth)}
            />
          ))}
        </div>
      )}
    </ScSliderContainer>
  ) : null;
}
